import {
  Button,
  Card,
  Flex,
  List,
  Modal,
  Popconfirm,
  Space,
  Typography,
} from "antd";
import { HelpCircle, Pencil, Settings, Trash2 } from "lucide-react";
import { useState } from "react";
import { ExamQuestionGroupForm } from "./ExamQuestionGroupForm";
import { ExamQuestionGroupSettingsForm } from "./ExamQuestionGroupSettingsForm";
import { ExamGroup } from "@types";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useTranslation } from "react-i18next";
import { useDeleteExamGroup } from "@hooks";
import { UniqueIdentifier } from "@dnd-kit/core";
import { useCanDeleteExamGroup } from "@/hooks/exam/group/useCanDeleteExamGroup";

interface Props {
  questionGroup: ExamGroup;
  onClick?: React.MouseEventHandler<HTMLDivElement> | undefined;
  selectedGroup?: ExamGroup;
  isDragging?: boolean;
  index: number;
  activeId?: UniqueIdentifier;
  examPeriodScreen?: boolean;
}

export const ExamQuestionGroupItemList = ({
  questionGroup,
  onClick,
  selectedGroup,
  index,
  isDragging,
  activeId,
  examPeriodScreen
}: Props) => {
  const {t} = useTranslation();
  const deleteExamGroup = useDeleteExamGroup();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isModalGroupSettings, setIsModalGroupSettings] = useState(false);
  const [checkDelete, setCheckDelete] = useState(false);
  const canDeleteExamGroup = useCanDeleteExamGroup(questionGroup.id, checkDelete);
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({
      id: questionGroup.id,
      // animateLayoutChanges: () => false,
      // transition: {
      //   duration: 250,
      //   easing: "ease"
      // }
    });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: (!isDragging && activeId === questionGroup?.id) ? 0.5 : 1,
    border: selectedGroup?.id === questionGroup.id ? "1px solid #F1B80E" : "",
    width: "100%",
    cursor: isDragging ? 'grabbing' : 'grab',
    boxShadow: isDragging  ? '' : 'rgb(63 63 68 / 5%) 0px 0px 0px 1px, rgb(34 33 81 / 15%) 0px 1px 3px 0px',
  };

  return (
    <List.Item style={{ width: "100%",  }}>
      <Card
        ref={setNodeRef}
        bordered
        {...attributes}
        {...listeners}
        onClick={onClick}
        style={style}
      >
        <Flex justify="space-between" align="center">
          <Space direction="vertical">
            <Typography.Title level={5} style={{ margin: 0 }}>
              {`${index+1}. ${questionGroup.name}`}
            </Typography.Title>
            <Typography.Text>{t("pages.examsManagment.examQuestionsTab.numQuestions")}: {questionGroup.numberOfQuestionsInGroup}</Typography.Text>
          </Space>

          <Space>
            {!examPeriodScreen && (<Button onClick={() => setIsModalOpen(true)} icon={<Pencil />} />)}
            <Button
              onClick={() => setIsModalGroupSettings(true)}
              icon={<Settings />}
            />
            {!examPeriodScreen && (<Popconfirm
              title={t("pages.examsManagment.deleteGroup")}
              description={t("pages.examsManagment.deleteGroupDesc")}
              onConfirm={() => deleteExamGroup.mutate(questionGroup.id)}
              okButtonProps={{loading: deleteExamGroup.isPending || canDeleteExamGroup.isPending, disabled: !canDeleteExamGroup.data}}
              onOpenChange={(val) => setCheckDelete(val)}
              icon={<HelpCircle style={{ color: "red", marginRight: 8 }} />}
            >
              <Button danger icon={<Trash2 />} />
            </Popconfirm>)}
          </Space>
        </Flex>
      </Card>
      <Modal
        title={t("pages.examsManagment.editGroup")}
        footer={null}
        onCancel={() => setIsModalOpen(false)}
        destroyOnClose
        open={isModalOpen}
      >
        <ExamQuestionGroupForm setIsModalOpen={setIsModalOpen} examGroup={questionGroup}/>
      </Modal>

      <Modal
        title={t("pages.examsManagment.groupSettings")}
        footer={null}
        onCancel={() => setIsModalGroupSettings(false)}
        destroyOnClose
        open={isModalGroupSettings}
      >
        <ExamQuestionGroupSettingsForm setIsModalGroupSettings={setIsModalGroupSettings} examGroup={questionGroup} examPeriodScreen={examPeriodScreen}/>
      </Modal>
    </List.Item>
  );
};
